<!-- TEMPLATE -->
<template>
  <dean-dialog
      class="postcode-checker"
      title="Postcode checker"
      :icon="dashboardIcon"
      align-top>
    <div v-if="!isLoading">
      <dean-input-field
          id="postalCode"
          v-model="postalCode"
          required-field="true"
          @input="changePostalCodeValue"
          text>
        Postcode
      </dean-input-field>
      <div class="row">
        <dean-select-form-row
            id="houseNumber"
            placeholder="kies een huisnummer"
            :value="houseNumber"
            :disabled="!this.postalCode || !this.postalCode.match(this.postalCodeValid)"
            :requiredField="true"
            :clearable="true"
            :hideLabel="true"
            :options="houseNumbers"
            @input="changeHouseNumberSelectValue($event)">
        </dean-select-form-row>
        <dean-select-form-row
            id="houseNumberExtension"
            placeholder="kies een toevoeging huisnummer"
            :value="houseNumberExtension"
            :disabled="!this.postalCode || !this.houseNumber || !this.postalCode.match(this.postalCodeValid)"
            :requiredField="false"
            :clearable="true"
            :hideLabel="true"
            :options="houseNumberExtensions"
            @input="changeHouseNumberExtensionSelectValue($event)">
        </dean-select-form-row>
      </div>
      <dean-button :primary="buttonClickEnabled" :disabled="!buttonClickEnabled" dialog-button @click.native="clickCall">
        Check
      </dean-button>
    </div>
    <transition name="fade" mode="out-in" v-if="isLoading">
      <dean-load-overlay></dean-load-overlay>
    </transition>
  </dean-dialog>
</template>
<!-- SCRIPT -->
<script lang="ts">
  import Vue from 'vue';
  import axios from 'axios';
  import {Component} from 'vue-property-decorator';
  import {deanDialogBus} from '@/main';

  // DEAN COMPONENTS
  import {
    dashboardIcon,
    deanIcon,
    deanInputField,
    deanDialog,
    deanButton,
    deanSpinner,
  } from '@deanone/dean-components';
  import DeanLoadOverlay from '@deanone/dean-components/src/components/deanLoadOverlay.vue';
  import {warningIcon} from '@deanone/dean-components';
  import DeanSelectFormRow from '@/components/deanComponents/deanSelectFormRow.vue';

  @Component({
    components: {
      DeanSelectFormRow,
      DeanLoadOverlay,
      dashboardIcon,
      deanIcon,
      deanInputField,
      deanDialog,
      deanButton,
      deanSpinner,
    },
  })

  export default class PostalCodeCheckerWidget extends Vue {
    public postalCodeValid = '^([0-9]{4})[ -]*([A-Za-z]{2})$';
    public houseNumberValid = '^([0-9]+|[0-9]+[a-zA-Z]+[0-9]*|[0-9]+-[0-9]+)$';
    public dashboardIcon: object = dashboardIcon;
    public inputFieldInvalid = true;
    public isLoading: boolean = false;
    public postalCode: string = '';
    public houseNumber: string = '';
    public houseNumbers: Array<{ value: any, label: any }> = [];
    public houseNumberExtension: string = '';
    public houseNumberExtensions: Array<{ value: any, label: any }> = [];
    public result: any = null;
    public columns: any = {
      type: {
        content: 'type verbinding',
        align: 'left',
        sort: true,
        dql: 'type',
      },
      download: {
        content: 'max download Mbps',
        align: 'left',
        sort: true,
        dql: 'download',
      },
      upload: {
        content: 'max upload Mbps',
        align: 'left',
        sort: true,
        dql: 'upload',
      },
    };

    public newCall() {
      this.result = null;
    }

    public showResult() {
      deanDialogBus.$emit('open', 'DetailsDialog', {
        title: 'Resultaten postcode check',
        data: this.result,
        columns: this.columns,
      });
      this.newCall();
    }

    public changePostalCodeValue() {
      this.houseNumber = '';
      this.houseNumbers = [];

      this.houseNumberExtension = '';
      this.houseNumberExtensions = [];

      this.inputFieldInvalid = true;

      if (!this.postalCode.match(this.postalCodeValid)) {
        return;
      }

      this.postalCodeNumberCheck();
    }

    public changeHouseNumberSelectValue(field: any) {
      this.houseNumber = field.value.toString();

      this.houseNumberExtension = '';
      this.houseNumberExtensions = [];

      this.inputFieldInvalid = true;

      if (!this.postalCode.match(this.postalCodeValid)) {
        return;
      }

      if (this.houseNumber && !this.houseNumber.match(this.houseNumberValid)) {
        return;
      }

      this.postalCodeNumberExtensionCheck();

      this.inputFieldInvalid = false;
    }

    public changeHouseNumberExtensionSelectValue(field: any) {
      this.houseNumberExtension = field.value;

      this.inputFieldInvalid = false;
    }

    public postalCodeCheck() {
      this.columns = {
        type: {
          content: 'type verbinding',
          align: 'left',
          sort: true,
          dql: 'type',
        },
        download: {
          content: 'max download Mbps',
          align: 'left',
          sort: true,
          dql: 'download',
        },
        upload: {
          content: 'max upload Mbps',
          align: 'left',
          sort: true,
          dql: 'upload',
        },
      };
      this.$toast({
        text: 'Het opvragen van deze data kan mogelijk langer duren, een moment geduld a.u.b.',
        theme: 'warning',
        icon: warningIcon,
        title: 'Attentie',
      });

      return axios
        .post('/-/trade/api/widgets.postalCodeChecker', {
          postalCode: this.postalCode,
          houseNumber: this.houseNumber,
          houseNumberExtension: this.houseNumberExtension,
        })
        .then(
          (response) => {
            this.result = response.data.map((data: any) => {
              return {...data, ...{
                // convert BPS to MBPS
                download: Math.round(data.maxBytesPerSecondDownload / 1000),
                upload: Math.round(data.maxBytesPerSecondUpload / 1000),
              }};
            });
            this.showResult();
          },
        );
    }

    public postalCodeNumberCheck() {
      return axios
      .post('/-/trade/api/widgets.postalCodeNumberChecker', {
        postalCode: this.postalCode,
      })
      .then(
        (response) => {
          this.houseNumbers = response.data.map((houseNumber: any) => {
            return {
              value: houseNumber,
              label: houseNumber,
            };
          });
        },
      );
    }

    public postalCodeNumberExtensionCheck() {
      return axios
          .post('/-/trade/api/widgets.postalCodeNumberExtensionChecker', {
            postalCode: this.postalCode,
            houseNumber: this.houseNumber,
          })
          .then(
              (response) => {
                this.houseNumberExtensions = response.data.map((houseNumberExtension: any) => {
                  return {
                    value: houseNumberExtension ? houseNumberExtension : '-',
                    label: houseNumberExtension ? houseNumberExtension : '-',
                  };
                });
              },
          );
    }

    public buttonClickEnabled() {
      if (this.inputFieldInvalid) {
        return false;
      }

      if (!this.postalCode || !this.postalCode.match(this.postalCodeValid)) {
        return false;
      }

      if (!this.houseNumber || !this.houseNumber.match(this.houseNumberValid)) {
        return false;
      }

      return true;
    }

    public clickCall(event: any) {
      if (!this.buttonClickEnabled()) {
        return;
      }

      let promise = null;

      this.isLoading = true;

      if (this.postalCode && this.houseNumber) {
        promise = this.postalCodeCheck();
      } else {
        return;
      }

      promise.catch((e) => {
        switch (e.response.data.errors && e.response.data.errors[0].code) {
            case 'ERR_INVALID_POSTAL_CODE':
              this.$toast(this.$store.state.main.notification.messages.postalCodeInvalid);
              break;
            default:
              this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
              break;
          }
      }).finally(() => {
        this.isLoading = false;
      });
    }
  }
</script>

<style lang="scss" scoped>
  // IMPORT
  @import '~@deanone/dean-components/src/scss/vars';

  .dialog :deep {
    padding: 0;
  }

  .dialog :deep(.dean-dialog-container) {
    width: 100%;
    height: 100%;
  }

  .dialog :deep(.dean-dialog-container .dean-dialog-content) {
    height: 100%;
  }

  .row {
    display: inline-flex;
    flex-direction: row;

    > .dean-form-row {
      width: 50%;
    }
  }
</style>
